/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Recovery for a route chunk that fails to download.
 *
 * Code-health survey `10-performance.md` #4 / `17-wasted-work.md` #5: the
 * pages behind the sign-in are lazy chunks. A chunk is fetched the first time
 * its route is visited, so a tab that was opened before a deploy asks the
 * server for a file name that the new build no longer emits, and the import
 * rejects. Merging to main deploys within minutes, so this is a routine event,
 * not an edge case. The remedy is a reload: the fresh `index.html` names the
 * chunks that exist now.
 *
 * The reload has to be bounded. If the server is genuinely broken, reloading on
 * every failure is a loop the user cannot escape, so the boundary reloads at
 * most once per incident and then shows the error card. `sessionStorage`
 * survives the reload and dies with the tab, which is exactly the lifetime the
 * guard needs.
 *
 * An incident ends when the tab proves it is healthy again: after the reload,
 * the first location change that arrives while the boundary holds no error
 * releases the guard. That says the route being left did not fail (it
 * rendered, or was still loading when the user moved on); the new route's own
 * chunk is not waited for. So a later, separate incident in the same tab gets
 * its own single reload. The guard is deliberately NOT released on mount,
 * because a deploy that is broken for every route would then reload on every
 * attempt.
 */

/** Set while an automatic reload for a missing chunk is in flight. */
export const CHUNK_RELOAD_KEY = 'lms.route-chunk-reloaded';

/**
 * Whether `error` is a page chunk that failed to load, as distinct from a bug
 * inside a page that did load. Vite / Chrome say "Failed to fetch dynamically
 * imported module", Firefox "error loading dynamically imported module",
 * Safari "Importing a module script failed"; webpack-built bundles name the
 * error `ChunkLoadError`. Vite's preload helper rejects with "Unable to
 * preload CSS for <url>" when a page chunk's own stylesheet is the file that
 * went missing, which is the same stale-tab incident seen one request earlier.
 * Anything else is not ours to handle.
 *
 * Vite also dispatches a cancelable `vite:preloadError` event for the CSS
 * case. It is deliberately not listened for: cancelling it makes the dynamic
 * import resolve to `undefined` (the helper swallows the error and returns
 * nothing), which `React.lazy` reports as a different, non-chunk error; and
 * not cancelling it means the same rejection reaches the boundary anyway, so a
 * listener would only claim the one reload before the boundary could.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === 'ChunkLoadError') return true;
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(
    error.message
  );
}

/** True when the previous page load was itself the automatic reload. */
export function reloadGuardSpent(): boolean {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_KEY) !== null;
  } catch {
    // Storage blocked (private mode, a policy). Treated as spent: without a
    // guard that can be written, an automatic reload could loop.
    return true;
  }
}

/**
 * Claims the one automatic reload for this incident.
 *
 * Returns true if the caller may reload now. Returns false, and clears the
 * guard, if a reload has already been spent, so that the card is shown this
 * time and a later, separate incident gets its own single reload. Also
 * returns false when storage cannot be written, for the reason above.
 */
export function armReloadGuard(): boolean {
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY) !== null) {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

/**
 * Ends the incident: a page rendered after the reload, so the tab is healthy
 * and the next missing chunk is a new incident with its own single reload.
 * Called by the boundary on the first successful navigation, never on mount.
 */
export function releaseReloadGuard(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // Nothing to release if storage is blocked; the guard reads as spent anyway.
  }
}
